/**
 * Alert Rules — direct SMS, email, and mobile push alerts.
 *
 * Unlike Slack/Teams webhooks, this requires NO third-party account.
 * Rules fire directly via Twilio SMS, ACS email, or Expo mobile push
 * when queue conditions exceed configured thresholds.
 *
 * Metrics supported:
 *   queue_depth  — queued conversations exceed N
 *   longest_wait — oldest queued conversation waited > N minutes
 *   missed_today — missed/abandoned conversations today > N
 *   all_offline  — all agents offline while conversations are open
 *   no_activity  — agents online but zero new conversations (outage detector)
 *   coverage_gap — N or more teams have no available agents
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import IntegrationActivityLog from "./IntegrationActivityLog";

interface AlertRule {
  id: number;
  siteId: number;
  name: string;
  metric: string;
  threshold: number;
  windowMinutes: number;
  notifyPhoneNumbers: string | null;
  notifyEmails: string | null;
  notifyMobileUserIds: string | null;
  minRefireMinutes: number;
  enabled: boolean;
  lastFiredAt: string | null;
  createdAt: string;
}

const METRIC_OPTIONS = [
  { value: "queue_depth",  label: "Queue Depth",                      hasThreshold: true,  thresholdLabel: "Queued conversations" },
  { value: "longest_wait", label: "Longest Wait (minutes)",           hasThreshold: true,  thresholdLabel: "Minutes waiting" },
  { value: "missed_today", label: "Missed / Abandoned Today",         hasThreshold: true,  thresholdLabel: "Count" },
  { value: "all_offline",  label: "All Agents Offline",               hasThreshold: false, thresholdLabel: "" },
  { value: "no_activity",  label: "No Activity (outage detector)",    hasThreshold: false, thresholdLabel: "" },
  { value: "coverage_gap", label: "Coverage Gap (teams uncovered)",   hasThreshold: true,  thresholdLabel: "Teams uncovered" },
];

const METRIC_LABELS: Record<string, string> = Object.fromEntries(
  METRIC_OPTIONS.map((m) => [m.value, m.label])
);

type FormState = Omit<AlertRule, "id" | "siteId" | "lastFiredAt" | "createdAt">;

const emptyForm = (): FormState => ({
  name: "",
  metric: "queue_depth",
  threshold: 5,
  windowMinutes: 5,
  notifyPhoneNumbers: "",
  notifyEmails: "",
  notifyMobileUserIds: "",
  minRefireMinutes: 60,
  enabled: true,
});

export default function AlertRules() {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [rules, setRules]         = useState<AlertRule[]>([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState<AlertRule | null>(null);
  const [form, setForm]           = useState<FormState>(emptyForm());
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState("");
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, { ok: boolean; msg: string }>>({});
  const [deleting, setDeleting]   = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await (await api.admin.get("OperationalAlerts")).json() as AlertRule[];
      setRules(Array.isArray(data) ? data : []);
    } catch {
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setSaveError("");
    setShowModal(true);
  }

  function openEdit(r: AlertRule) {
    setEditing(r);
    setForm({
      name: r.name,
      metric: r.metric,
      threshold: r.threshold,
      windowMinutes: r.windowMinutes,
      notifyPhoneNumbers: r.notifyPhoneNumbers ?? "",
      notifyEmails: r.notifyEmails ?? "",
      notifyMobileUserIds: r.notifyMobileUserIds ?? "",
      minRefireMinutes: r.minRefireMinutes,
      enabled: r.enabled,
    });
    setSaveError("");
    setShowModal(true);
  }

  async function save() {
    if (!form.name.trim()) { setSaveError("Name is required."); return; }
    if (!form.notifyPhoneNumbers?.trim() && !form.notifyEmails?.trim() && !form.notifyMobileUserIds?.trim()) {
      setSaveError("At least one notification channel is required (phone, email, or mobile push).");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      const res = editing
        ? await api.admin.put(`OperationalAlerts/${editing.id}`, form as Record<string, unknown>)
        : await api.admin.post("OperationalAlerts", form as Record<string, unknown>);
      const data = await res.json();
      if (!res.ok) { setSaveError((data as { error?: string })?.error ?? "Save failed."); return; }
      setShowModal(false);
      await load();
    } catch {
      setSaveError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(r: AlertRule) {
    try {
      await api.admin.put(`OperationalAlerts/${r.id}`, { ...r, enabled: !r.enabled } as Record<string, unknown>);
      await load();
    } catch { /* ignore */ }
  }

  async function del(id: number) {
    if (!confirm("Delete this alert rule?")) return;
    setDeleting(id);
    try {
      await api.admin.delete(`OperationalAlerts/${id}`);
      setRules(prev => prev.filter(x => x.id !== id));
    } catch { /* ignore */ } finally {
      setDeleting(null);
    }
  }

  async function test(id: number) {
    setTestingId(id);
    setTestResults(prev => ({ ...prev, [id]: undefined as any }));
    try {
      const res = await api.admin.post(`OperationalAlerts/${id}/test`, {} as Record<string, unknown>);
      const data = await res.json() as { sent?: boolean; error?: string };
      setTestResults(prev => ({ ...prev, [id]: { ok: !!data.sent, msg: data.sent ? "Test notification sent." : (data.error ?? "Test failed.") } }));
    } catch {
      setTestResults(prev => ({ ...prev, [id]: { ok: false, msg: "Test request failed." } }));
    } finally {
      setTestingId(null);
    }
  }

  if (!subscription?.enableOperationalAlerts) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <AlertBellIcon />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Alert Rules</h1>
            <p className="text-sm text-slate-500">
              Direct SMS, email, and mobile push — no Slack or Teams account required.
            </p>
          </div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <p className="text-sm font-medium text-amber-800 mb-1">Professional plan required</p>
          <p className="text-sm text-amber-700">
            Operational alert rules are available on the Professional plan and above.
            Contact your account manager to upgrade.
          </p>
        </div>
      </div>
    );
  }

  const metricInfo = METRIC_OPTIONS.find(m => m.value === form.metric);
  const notifyBadges = (r: AlertRule) => [
    r.notifyPhoneNumbers && "SMS",
    r.notifyEmails       && "Email",
    r.notifyMobileUserIds && "Push",
  ].filter(Boolean) as string[];

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <AlertBellIcon />
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Alert Rules</h1>
            <p className="text-sm text-slate-500">
              Get notified directly via SMS, email, or mobile push when queue conditions exceed thresholds.
              No Slack or Teams account required.
            </p>
          </div>
        </div>
        <PrimaryBlueButton label="Add Rule" onClick={openCreate} />
      </div>

      {/* How-to */}
      <SetupInstructions />

      {/* Rule list */}
      {loading ? (
        <div className="text-sm text-slate-400 py-4">Loading…</div>
      ) : rules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <p className="text-sm font-medium text-slate-600 mb-1">No alert rules configured yet</p>
          <p className="text-xs text-slate-400 mb-4">
            Add a rule to receive SMS or email when your queue gets too long or agents go offline.
          </p>
          <PrimaryBlueButton label="Add Your First Rule" onClick={openCreate} />
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(r => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex h-2 w-2 rounded-full flex-shrink-0 ${r.enabled ? "bg-green-500" : "bg-slate-300"}`} />
                    <span className="font-medium text-slate-800 text-sm">{r.name || "Unnamed"}</span>
                    {!r.enabled && (
                      <span className="text-xs text-slate-400 bg-slate-100 px-1.5 rounded">Disabled</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-1.5">
                    {METRIC_LABELS[r.metric] ?? r.metric}
                    {r.metric !== "all_offline" && r.metric !== "no_activity" && (
                      <span className="text-slate-400 ml-1">&gt; {r.threshold}</span>
                    )}
                    {" · "}window {r.windowMinutes}m · refire every {r.minRefireMinutes}m
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {notifyBadges(r).map(b => (
                      <span key={b} className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                        b === "SMS"   ? "bg-blue-100 text-blue-700"   :
                        b === "Email" ? "bg-green-100 text-green-700" :
                        "bg-purple-100 text-purple-700"
                      }`}>{b}</span>
                    ))}
                  </div>
                  {r.lastFiredAt && (
                    <p className="text-xs text-slate-400 mt-1">
                      Last fired: {new Date(r.lastFiredAt).toLocaleString()}
                    </p>
                  )}
                  {testResults[r.id] && (
                    <p className={`mt-2 text-xs ${testResults[r.id].ok ? "text-green-700" : "text-red-600"}`}>
                      {testResults[r.id].ok ? "✓ " : "✗ "}{testResults[r.id].msg}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    className="text-xs text-blue-600 hover:underline disabled:opacity-40"
                    onClick={() => test(r.id)}
                    disabled={testingId === r.id}
                  >
                    {testingId === r.id ? "Testing…" : "Test"}
                  </button>
                  <button
                    className="text-xs text-slate-500 hover:underline"
                    onClick={() => toggleEnabled(r)}
                  >
                    {r.enabled ? "Disable" : "Enable"}
                  </button>
                  <button
                    className="text-xs text-slate-500 hover:underline"
                    onClick={() => openEdit(r)}
                  >
                    Edit
                  </button>
                  <button
                    className="text-xs text-red-500 hover:underline disabled:opacity-40"
                    onClick={() => del(r.id)}
                    disabled={deleting === r.id}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="OperationalAlerts" />
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-slate-800 mb-5">
              {editing ? "Edit Alert Rule" : "New Alert Rule"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="e.g. Queue too deep, All agents offline"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Metric</label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.metric}
                  onChange={e => setForm(f => ({ ...f, metric: e.target.value }))}
                >
                  {METRIC_OPTIONS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                {form.metric === "no_activity" && (
                  <p className="text-xs text-slate-500 mt-1.5 bg-blue-50 border border-blue-100 rounded px-3 py-2">
                    Fires when agents are online but zero new conversations arrive within the window —
                    the signature of a broken widget, misconfigured routing, or Twilio number failure.
                  </p>
                )}
                {form.metric === "all_offline" && (
                  <p className="text-xs text-slate-500 mt-1.5">
                    Fires when all agents are offline and there are open conversations waiting.
                  </p>
                )}
              </div>

              {metricInfo?.hasThreshold && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Threshold ({metricInfo.thresholdLabel})
                  </label>
                  <input
                    type="number"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    value={form.threshold}
                    min={0}
                    onChange={e => setForm(f => ({ ...f, threshold: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Window (minutes)</label>
                <input
                  type="number"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.windowMinutes}
                  min={1}
                  onChange={e => setForm(f => ({ ...f, windowMinutes: parseInt(e.target.value) || 5 }))}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Condition must persist for this many minutes before the alert fires.
                </p>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-medium text-slate-600 mb-3">Notify via (at least one required)</p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Phone numbers — SMS (comma-separated E.164)</label>
                    <input
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={form.notifyPhoneNumbers ?? ""}
                      onChange={e => setForm(f => ({ ...f, notifyPhoneNumbers: e.target.value }))}
                      placeholder="+14436295872, +12223334444"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Email addresses (comma-separated)</label>
                    <input
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={form.notifyEmails ?? ""}
                      onChange={e => setForm(f => ({ ...f, notifyEmails: e.target.value }))}
                      placeholder="ops@yourco.com, manager@yourco.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Agent IDs for mobile push (comma-separated)</label>
                    <input
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={form.notifyMobileUserIds ?? ""}
                      onChange={e => setForm(f => ({ ...f, notifyMobileUserIds: e.target.value }))}
                      placeholder="42, 107"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Min refire interval (minutes)</label>
                <input
                  type="number"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={form.minRefireMinutes}
                  min={1}
                  onChange={e => setForm(f => ({ ...f, minRefireMinutes: parseInt(e.target.value) || 60 }))}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Alerts won't re-fire until this many minutes have passed since the last notification.
                </p>
              </div>

              {editing && (
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600"
                    checked={form.enabled}
                    onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                  />
                  Rule enabled
                </label>
              )}

              {saveError && (
                <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{saveError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <SecondaryBlueButton label="Cancel" onClick={() => setShowModal(false)} disabled={saving} />
              <PrimaryBlueButton label={saving ? "Saving…" : "Save"} onClick={save} disabled={saving} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AlertBellIcon() {
  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-orange-500 flex-shrink-0">
      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    </div>
  );
}

function SetupInstructions() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-5 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>How alert rules work</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-slate-100 text-sm text-slate-600 space-y-3">
          <ol className="mt-4 space-y-3">
            {[
              { n: "1", text: "Choose a metric (queue depth, longest wait, missed today, all offline, no activity, or coverage gap)." },
              { n: "2", text: "Set a threshold and window — the alert fires when the condition is true for the entire window." },
              { n: "3", text: "Enter at least one notification target: phone number for SMS, email address, or agent ID for mobile push." },
              { n: "4", text: "Set the refire interval to prevent notification floods — e.g. 60 minutes means one alert per hour maximum." },
              { n: "5", text: "Click Test to send a real notification immediately and verify delivery." },
            ].map(s => (
              <li key={s.n} className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-orange-500 text-white text-xs font-bold mt-0.5">{s.n}</span>
                <p className="text-xs text-slate-600 leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800 mt-3">
            <strong>No activity detector:</strong> The <em>No Activity</em> metric fires when agents are online
            but zero new conversations have arrived — the signature of a broken widget, misconfigured routing,
            or a Twilio number failure. It is the fastest way to catch a silent outage before customers notice.
          </div>
        </div>
      )}
    </div>
  );
}
